// src/Confirmation.js - Updated for Professionalism (Uses Auth State for Email)
import React, { useState, useEffect } from "react"; // useEffect needed for auth listener
import { useNavigate } from "react-router-dom"; // Keep useNavigate if used (e.g., for optional redirect)
import { getAuth, onAuthStateChanged } from "firebase/auth"; // Import auth functions
import { app } from "./firebaseConfig"; // Assuming app is exported from firebaseConfig
import "./styles.css"; // Assuming your styles are here

const auth = getAuth(app); // Initialize Firebase Auth

function Confirmation() {
  const navigate = useNavigate();

  // State for authentication
  const [verifiedEmail, setVerifiedEmail] = useState(null); // Store email from Auth state
  const [isLoadingAuth, setIsLoadingAuth] = useState(true); // Loading indicator for auth check

  // State for feedback form
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // --- Authentication Check Effect ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        setVerifiedEmail(user.email);
      } else {
        // User is signed out or session is invalid
        setVerifiedEmail(null); // Explicitly set to null if no user
        console.log("No authenticated user on confirmation page.");
        // Decide if you want to redirect here or just not show the email
        // navigate('/'); // Optional: Redirect if auth is strictly required even on confirmation
      }
      setIsLoadingAuth(false); // Auth check complete
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]); // Added navigate as dependency, though redirect is optional here
  // --- End Authentication Check ---

  const feedbackScriptUrl = "https://script.google.com/macros/s/AKfycbz2QKzIi88lAbnA3Y20ducsF7FwsX6EF30Qi1_Nkb8DiuMvmZ3VyeLuqimuv2A3qOn5Fg/exec"; // Replace if yours is different!

  // --- Logic for Half-Star Rating --- (Unchanged from previous version)
  const calculateRatingFromMouseEvent = (starValue, event) => {
    const starElement = event.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const starWidth = rect.width;
    return mouseX < starWidth / 2 ? starValue - 0.5 : starValue;
  };
  const handleMouseMove = (starValue, event) => { setHoverRating(calculateRatingFromMouseEvent(starValue, event)); };
  const handleMouseLeave = () => { setHoverRating(0); };
  const handleClick = (starValue, event) => { setRating(calculateRatingFromMouseEvent(starValue, event)); };
  // --- End of Half-Star Logic ---

  // Handle feedback submission - ** USES verifiedEmail **
  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    // Check rating and ensure we have the verified email before submitting
    if (!rating) {
      alert("Please select a star rating.");
      return;
    }
    if (!verifiedEmail) {
        alert("Could not verify user email. Please try refreshing.");
        return;
    }
    if (isSubmittingFeedback) return;
    setIsSubmittingFeedback(true);

    try {
      await fetch(feedbackScriptUrl, {
        method: "POST",
        mode: "no-cors",
        // Send the securely obtained verifiedEmail
        body: JSON.stringify({ email: verifiedEmail, rating, feedback }),
        headers: { "Content-Type": "application/json" }
      });
      setSubmitted(true);
      // Optional: Redirect after a delay
      // setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      alert("Failed to submit feedback. Please try again.");
    } finally {
       setIsSubmittingFeedback(false);
    }
  };

  // Render loading state while checking authentication
  if (isLoadingAuth) {
    return <div className="loading-container" style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>;
  }

  // Render the main confirmation content
  return (
    <div className="confirmation-container">
      <h1>✅ Submission Successful!</h1>
      {/* Only show email confirmation if email was verified */}
      {verifiedEmail && (
        <p>You should receive a confirmation email at <b>{verifiedEmail}</b> shortly.</p>
      )}
      {!verifiedEmail && (
          <p>Your submission has been received.</p> // Fallback message
      )}

      {!submitted ? (
        <form className="feedback-form" onSubmit={handleSubmitFeedback}>
          <h3>Rate your experience</h3>
          <div className="stars" onMouseLeave={handleMouseLeave}>
            {[1, 2, 3, 4, 5].map((starValue) => {
               const currentDisplayRating = hoverRating || rating;
               let fillPercentage = '0%';
               if (currentDisplayRating >= starValue) fillPercentage = '100%';
               else if (currentDisplayRating >= starValue - 0.5) fillPercentage = '50%';
               return (
                 <span key={starValue} className="star-container"
                   onMouseMove={(e) => handleMouseMove(starValue, e)}
                   onClick={(e) => handleClick(starValue, e)} >
                   <span className="star-background">☆</span>
                   <span className="star-foreground" style={{ width: fillPercentage }}>★</span>
                 </span>
               );
             })}
          </div>
          <textarea
            placeholder="Anything you'd like to share? (Optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <button type="submit" disabled={isSubmittingFeedback || !verifiedEmail}>
            {isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
          </button>
        </form>
      ) : (
         <p className="thank-you">
           Thank you! Your insights help shape PeerfectCV. For specific queries or more detailed feedback, please feel free to reach out at <a href="mailto:hello@peerfectcv.com">hello@peerfectcv.com</a>.
         </p>
      )}
    </div>
  );
}

export default Confirmation;